import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  Line,
  LineChart
} from 'recharts';

import { apiService } from '../services/api';
import {
  CPUMetricsResponse,
  MemoryMetricsResponse,
  NetworkMetricsResponse,
  StorageMetricsResponse,
  MetricSeries,
  InstantMetric
} from '../types/api';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

// ============================================
// Helpers: transform raw Prometheus data → Recharts format
// ============================================

interface ChartPoint {
  timestamp: string;
  [key: string]: string | number;
}

/** Convert MetricSeries[] → Recharts-friendly array, one line per pod. */
function seriesToChartData(seriesList: MetricSeries[]): ChartPoint[] {
  if (!seriesList || seriesList.length === 0) return [];

  const map = new Map<number, ChartPoint>();

  seriesList.forEach((series) => {
    const pod = series.metric.pod || series.metric.container || 'total';
    series.values.forEach(([ts, val]) => {
      if (!map.has(ts)) {
        const date = new Date(ts * 1000);
        map.set(ts, {
          timestamp: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString(),
        });
      }
      map.get(ts)![pod] = parseFloat(val);
    });
  });

  return Array.from(map.values());
}

/** Sum all pod instant values into a single number. */
function getInstantTotal(metrics: InstantMetric[]): number | null {
  if (!metrics || metrics.length === 0) return null;
  return metrics.reduce((sum, m) => sum + parseFloat(m.value[1] || '0'), 0);
}

/** Get the first pod's instant value as a number. */
function getFirstInstantValue(metrics: InstantMetric[]): number | null {
  if (!metrics || metrics.length === 0) return null;
  return parseFloat(metrics[0].value[1] || '0');
}

/** Get distinct pod names from a MetricSeries[]. */
function getPodNames(seriesList: MetricSeries[]): string[] {
  if (!seriesList) return [];
  return Array.from(new Set(seriesList.map((s) => s.metric.pod || s.metric.container || 'total')));
}

// Chart color palette
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// ============================================
// Component
// ============================================

interface MonitoringContentProps {
  deploymentId: string;
}

const MonitoringContent: React.FC<MonitoringContentProps> = ({ deploymentId }) => {
  const { t } = (useTranslation as any)('monitoring');
  const [loading, setLoading] = useState(true);
  const [namespace, setNamespace] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [applicationName, setApplicationName] = useState<string>('');

  // Metrics data
  const [cpuMetrics, setCpuMetrics] = useState<CPUMetricsResponse | null>(null);
  const [memoryMetrics, setMemoryMetrics] = useState<MemoryMetricsResponse | null>(null);
  const [networkMetrics, setNetworkMetrics] = useState<NetworkMetricsResponse | null>(null);
  const [storageMetrics, setStorageMetrics] = useState<StorageMetricsResponse | null>(null);

  // Time range selection
  const [timeRange, setTimeRange] = useState<string>('1h');
  const [refreshInterval, setRefreshInterval] = useState<number | null>(30000);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Load deployment details to get namespace and tenant_id
  useEffect(() => {
    const fetchDeploymentDetails = async () => {
      try {
        const details = await apiService.getDeploymentDetails(deploymentId);
        if (details.namespace) setNamespace(details.namespace);
        if (details.tenant_id) setTenantId(details.tenant_id);
        if (details.application_name) setApplicationName(details.application_name);
      } catch (error) {
        Logger.error('Failed to load deployment details', error);
        toast.error(t('loadDetailsError') as string);
      }
    };

    fetchDeploymentDetails();
  }, [deploymentId]);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    if (!deploymentId || !namespace || !tenantId) {
      return;
    }

    try {
      const startTime = `now-${timeRange}`;
      const endTime = 'now';
      const stepMap: Record<string, string> = {
        '15m': '15s',
        '1h': '30s',
        '6h': '5m',
        '24h': '15m',
        '7d': '1h',
      };
      const step = stepMap[timeRange] || '5m';

      const [cpu, memory, network, storage] = await Promise.all([
        apiService.getDeploymentCPUMetrics(deploymentId, namespace, tenantId, startTime, endTime, step),
        apiService.getDeploymentMemoryMetrics(deploymentId, namespace, tenantId, startTime, endTime, step),
        apiService.getDeploymentNetworkMetrics(deploymentId, namespace, tenantId, startTime, endTime, step),
        apiService.getDeploymentStorageMetrics(deploymentId, namespace, tenantId, startTime, endTime, step)
      ]);

      setCpuMetrics(cpu);
      setMemoryMetrics(memory);
      setNetworkMetrics(network);
      setStorageMetrics(storage);
      setLoading(false);
    } catch (error: unknown) {
      Logger.error('Failed to fetch metrics', error);
      toast.error(t('loadError') as string);
      setLoading(false);
    }
  }, [deploymentId, namespace, tenantId, timeRange]);

  // Initial metrics fetch
  useEffect(() => {
    if (namespace && tenantId) {
      fetchMetrics();
    }
  }, [fetchMetrics, namespace, tenantId]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh || !refreshInterval) {
      return;
    }

    const interval = setInterval(() => {
      fetchMetrics();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchMetrics]);

  // Formatters
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatCPU = (value: number) => {
    if (value >= 1) return `${value.toFixed(2)} cores`;
    return `${(value * 1000).toFixed(0)}m`;
  };

  const formatBytesPerSec = (value: number) => {
    return `${formatBytes(value)}/s`;
  };

  // Affiche le nom de l'application au lieu du nom de pod K8s
  // (ex: "zari" au lieu de "app-zari-9049b2f2-nubi-code-app-56fcc65bc8-b56bt").
  // Si plusieurs replicas, distinguer par un index 1-based (pas le hash K8s
  // qui change a chaque rolling update).
  const formatPodLabel = (pod: string, index: number, total: number): string => {
    const base = applicationName || pod;
    return total > 1 ? `${base} (${index + 1})` : base;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-text-secondary">{t('loadingMetrics')}</div>
      </div>
    );
  }

  // Prepare chart data from timeseries (if available)
  const cpuChartData = cpuMetrics?.timeseries?.usage
    ? seriesToChartData(cpuMetrics.timeseries.usage)
    : [];
  const cpuPods = cpuMetrics?.timeseries?.usage
    ? getPodNames(cpuMetrics.timeseries.usage)
    : [];

  const memoryChartData = memoryMetrics?.timeseries?.usage
    ? seriesToChartData(memoryMetrics.timeseries.usage)
    : [];
  const memoryPods = memoryMetrics?.timeseries?.usage
    ? getPodNames(memoryMetrics.timeseries.usage)
    : [];

  const networkReceiveData = networkMetrics?.timeseries?.receive_bandwidth
    ? seriesToChartData(networkMetrics.timeseries.receive_bandwidth)
    : [];
  const networkTransmitData = networkMetrics?.timeseries?.transmit_bandwidth
    ? seriesToChartData(networkMetrics.timeseries.transmit_bandwidth)
    : [];

  // Merge receive + transmit into a single dataset for the chart
  const networkChartData: ChartPoint[] = [];
  if (networkReceiveData.length > 0 || networkTransmitData.length > 0) {
    const tsMap = new Map<string, ChartPoint>();
    networkReceiveData.forEach((p) => {
      tsMap.set(p.timestamp, { ...p });
      Object.keys(p).forEach((k) => {
        if (k !== 'timestamp') {
          const point = tsMap.get(p.timestamp)!;
          delete point[k];
          point[`recv_${k}`] = p[k];
        }
      });
    });
    networkTransmitData.forEach((p) => {
      if (!tsMap.has(p.timestamp)) {
        tsMap.set(p.timestamp, { timestamp: p.timestamp });
      }
      const point = tsMap.get(p.timestamp)!;
      Object.keys(p).forEach((k) => {
        if (k !== 'timestamp') {
          point[`xmit_${k}`] = p[k];
        }
      });
    });
    networkChartData.push(...Array.from(tsMap.values()));
  }

  const storageIopsData = storageMetrics?.timeseries?.iops
    ? seriesToChartData(storageMetrics.timeseries.iops)
    : [];
  const storageThroughputData = storageMetrics?.timeseries?.throughput
    ? seriesToChartData(storageMetrics.timeseries.throughput)
    : [];

  // Instant values for summary cards
  const cpuUsageTotal = cpuMetrics ? getInstantTotal(cpuMetrics.usage_by_pod) : null;
  const cpuLimitTotal = cpuMetrics ? getInstantTotal(cpuMetrics.limits_by_pod) : null;
  const memUsageTotal = memoryMetrics ? getInstantTotal(memoryMetrics.usage_by_pod) : null;
  const memLimitTotal = memoryMetrics ? getInstantTotal(memoryMetrics.limits_by_pod) : null;
  const netRecvBw = networkMetrics ? getFirstInstantValue(networkMetrics.receive_bandwidth) : null;
  const netXmitBw = networkMetrics ? getFirstInstantValue(networkMetrics.transmit_bandwidth) : null;

  return (
    <>
      {/* Controls */}
      <div className="bg-surface-2 rounded-card shadow-card p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              {t('period')}
            </label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="block w-full px-3 py-2 border border-border-1 rounded-md shadow-card focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
            >
              <option value="15m">15 minutes</option>
              <option value="1h">1 heure</option>
              <option value="6h">6 heures</option>
              <option value="24h">24 heures</option>
              <option value="7d">7 jours</option>
            </select>
          </div>

          {/* Auto Refresh */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              {t('autoRefresh')}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-border-1 rounded"
              />
              <select
                value={refreshInterval || ''}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={!autoRefresh}
                className="block px-3 py-2 border border-border-1 rounded-md shadow-card focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary disabled:opacity-50"
              >
                <option value="10000">10s</option>
                <option value="30000">30s</option>
                <option value="60000">1 min</option>
                <option value="300000">5 min</option>
              </select>
            </div>
          </div>

          {/* Manual Refresh Button */}
          <div className="flex-grow flex justify-end items-end">
            <button
              onClick={fetchMetrics}
              className="bg-accent-500 hover:bg-accent-700 text-white font-bold py-2 px-4 rounded"
            >
              <ArrowPathIcon className="w-4 h-4 inline mr-1" /> {t('refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CPU Chart */}
        <div className="bg-surface-2 rounded-card shadow-card p-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            {t('cpuUsage')}
          </h3>
          {cpuChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={cpuChartData}>
                <defs>
                  {cpuPods.map((pod, i) => (
                    <linearGradient key={pod} id={`colorCPU_${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis tickFormatter={formatCPU} />
                <Tooltip formatter={(value: number) => formatCPU(value)} />
                {cpuPods.length > 1 && <Legend />}
                {cpuPods.map((pod, i) => (
                  <Area
                    key={pod}
                    type="monotone"
                    dataKey={pod}
                    stroke={COLORS[i % COLORS.length]}
                    fillOpacity={1}
                    fill={`url(#colorCPU_${i})`}
                    name={formatPodLabel(pod, i, cpuPods.length)}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-text-tertiary">
              {cpuUsageTotal !== null
                ? `${t('currentUsage')} ${formatCPU(cpuUsageTotal)} ${t('noHistoricalData')}`
                : t('noDataAvailable') as string}
            </div>
          )}
          {cpuMetrics && (
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              {cpuUsageTotal !== null && (
                <div>
                  <span className="text-text-secondary">{t('usage')}</span>
                  <span className="ml-2 font-medium text-text-primary">
                    {formatCPU(cpuUsageTotal)}
                  </span>
                </div>
              )}
              {cpuLimitTotal !== null && (
                <div>
                  <span className="text-text-secondary">{t('allocated')}</span>
                  <span className="ml-2 font-medium text-text-primary">
                    {formatCPU(cpuLimitTotal)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Memory Chart */}
        <div className="bg-surface-2 rounded-card shadow-card p-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            {t('memoryUsage')}
          </h3>
          {memoryChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={memoryChartData}>
                <defs>
                  {memoryPods.map((pod, i) => (
                    <linearGradient key={pod} id={`colorMem_${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[(i + 1) % COLORS.length]} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS[(i + 1) % COLORS.length]} stopOpacity={0}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis tickFormatter={formatBytes} />
                <Tooltip formatter={(value: number) => formatBytes(value)} />
                {memoryPods.length > 1 && <Legend />}
                {memoryPods.map((pod, i) => (
                  <Area
                    key={pod}
                    type="monotone"
                    dataKey={pod}
                    stroke={COLORS[(i + 1) % COLORS.length]}
                    fillOpacity={1}
                    fill={`url(#colorMem_${i})`}
                    name={formatPodLabel(pod, i, memoryPods.length)}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-text-tertiary">
              {memUsageTotal !== null
                ? `${t('currentUsage')} ${formatBytes(memUsageTotal)} ${t('noHistoricalData')}`
                : t('noDataAvailable') as string}
            </div>
          )}
          {memoryMetrics && (
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              {memUsageTotal !== null && (
                <div>
                  <span className="text-text-secondary">Usage:</span>
                  <span className="ml-2 font-medium text-text-primary">
                    {formatBytes(memUsageTotal)}
                  </span>
                </div>
              )}
              {memLimitTotal !== null && (
                <div>
                  <span className="text-text-secondary">Allouées:</span>
                  <span className="ml-2 font-medium text-text-primary">
                    {formatBytes(memLimitTotal)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Network Chart */}
        <div className="bg-surface-2 rounded-card shadow-card p-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            {t('networkTraffic')}
          </h3>
          {networkChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={networkChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis tickFormatter={formatBytesPerSec} />
                <Tooltip formatter={(value: number) => formatBytesPerSec(value)} />
                <Legend />
                {(() => {
                  const recvKeys = Object.keys(networkChartData[0] || {}).filter((k) => k.startsWith('recv_'));
                  return recvKeys.map((k, i) => (
                    <Line
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stroke="#f59e0b"
                      name={`${t('received')} (${formatPodLabel(k.replace('recv_', ''), i, recvKeys.length)})`}
                      dot={false}
                    />
                  ));
                })()}
                {(() => {
                  const xmitKeys = Object.keys(networkChartData[0] || {}).filter((k) => k.startsWith('xmit_'));
                  return xmitKeys.map((k, i) => (
                    <Line
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stroke="#8b5cf6"
                      name={`${t('sent')} (${formatPodLabel(k.replace('xmit_', ''), i, xmitKeys.length)})`}
                      dot={false}
                    />
                  ));
                })()}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-text-tertiary">
              {netRecvBw !== null || netXmitBw !== null ? (
                <div className="text-center">
                  {netRecvBw !== null && <div>{t('received')} : {formatBytesPerSec(netRecvBw)}</div>}
                  {netXmitBw !== null && <div>{t('sent')} : {formatBytesPerSec(netXmitBw)}</div>}
                  <div className="text-xs mt-1">{t('noHistoricalData')}</div>
                </div>
              ) : (
                t('noDataAvailable')
              )}
            </div>
          )}
        </div>

        {/* Storage IO Chart */}
        <div className="bg-surface-2 rounded-card shadow-card p-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Storage I/O
          </h3>
          {storageIopsData.length > 0 || storageThroughputData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={storageIopsData.length > 0 ? storageIopsData : storageThroughputData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis
                  tickFormatter={storageIopsData.length > 0
                    ? (v: number) => `${v.toFixed(0)} IOPS`
                    : formatBytesPerSec
                  }
                />
                <Tooltip
                  formatter={(value: number) =>
                    storageIopsData.length > 0
                      ? `${value.toFixed(2)} IOPS`
                      : formatBytesPerSec(value)
                  }
                />
                <Legend />
                {Object.keys((storageIopsData.length > 0 ? storageIopsData : storageThroughputData)[0] || {})
                  .filter((k) => k !== 'timestamp')
                  .map((k, i) => (
                    <Line
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stroke={COLORS[(i + 3) % COLORS.length]}
                      name={k}
                      dot={false}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-text-tertiary">
              {storageMetrics && storageMetrics.iops_total.length > 0 ? (
                <div className="text-center">
                  <div>IOPS: {getInstantTotal(storageMetrics.iops_total)?.toFixed(2)}</div>
                  <div>Throughput: {formatBytesPerSec(getInstantTotal(storageMetrics.throughput_total) || 0)}</div>
                  <div className="text-xs mt-1">{t('noHistoricalData')}</div>
                </div>
              ) : (
                t('noDataAvailable') as string
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MonitoringContent;
